/**
 * Privilege IDs from api/GetUserPrivileges and api/previllages (GetAllPrevilagesInList).
 * Used to show/hide UI and enable/disable actions based on user's assigned privileges.
 */

export const PRIVILEGE = {
  // Transactions - Sales & Invoices
  INVOICE_CREATION: 1,
  INVOICE_REPRINT: 2,
  REFUNDS_CREATION: 4,
  INVOICE_DELETE: 54,
  INVOICE_EDIT: 59,
  INVOICE_PRICE_CHANGE: 1086,
  EDIT_DISCOUNT: 1084,
  INVOICE_SEQUENCE: 36,

  // Customers
  CUSTOMER_CREATION: 5,
  DELETE_CUSTOMER: 7,
  UPDATE_CUSTOMER: 8,
  VIEW_CUSTOMER: 9,
  CUSTOMER_LEDGER: 45,

  // Company / Administration
  COMPANY_CREATION: 24,
  COMPANY_DELETE: 25,
  COMPANY_UPDATE: 26,
  COMPANY_VIEW: 27,

  // Users & Roles
  USER_CREATE: 46,
  USER_DELETE: 47,
  ROLE_CREATE: 48,
  ROLE_DELETE: 49,

  // Suppliers
  SUPPLIER_CREATION: 50,
  SUPPLIER_DELETE: 51,
  SUPPLIER_UPDATE: 52,
  SUPPLIER_VIEW: 53,

  // Reports
  EOD_REPORT_VIEW: 55,
  /** When present, show chart type options and Daily/Weekly/Monthly statistics charts on EOD Closure Report. */
  EOD_REPORT_GRAPHS: 1088,
  SETTINGS: 1070,

  // Inventory / Products
  PRODUCT_CREATE: 30,
  PRODUCT_DELETE: 31,
  PRODUCT_VIEW: 32,
  PRODUCT_UPDATE: 33,
  QUANTITY_UPDATE: 34,

  // Locations (Inventory)
  ADD_LOCATIONS: 1065,

  /** When present, user can select "All" for Company and Location filters; otherwise only specific company/location. */
  ALL_BRANCHES: 61,
} as const;

export type PrivilegeId = (typeof PRIVILEGE)[keyof typeof PRIVILEGE];

/** Privilege IDs required to show each main sidebar tab (any of these = show tab). */
export const TAB_VISIBILITY_PRIVILEGES: Record<string, PrivilegeId[]> = {
  transactions: [
    PRIVILEGE.INVOICE_CREATION,
    PRIVILEGE.REFUNDS_CREATION,
    PRIVILEGE.CUSTOMER_CREATION,
    PRIVILEGE.VIEW_CUSTOMER,
    PRIVILEGE.PRODUCT_CREATE,
    PRIVILEGE.PRODUCT_VIEW,
    PRIVILEGE.SUPPLIER_CREATION,
    PRIVILEGE.SUPPLIER_VIEW,
    PRIVILEGE.CUSTOMER_LEDGER,
  ],
  reports: [
    PRIVILEGE.INVOICE_REPRINT,
    PRIVILEGE.EOD_REPORT_VIEW,
    PRIVILEGE.CUSTOMER_LEDGER,
  ],
  administrator: [
    PRIVILEGE.COMPANY_CREATION,
    PRIVILEGE.COMPANY_VIEW,
    PRIVILEGE.USER_CREATE,
    PRIVILEGE.SETTINGS,
  ],
};

/** Privilege ID that controls visibility of each transaction card (show card if user has this). */
export const TRANSACTION_CARD_PRIVILEGE: Record<string, PrivilegeId> = {
  'Sales Invoice': PRIVILEGE.INVOICE_CREATION,
  'Refunds': PRIVILEGE.REFUNDS_CREATION,
  'Customers': PRIVILEGE.VIEW_CUSTOMER,
  'Inventory': PRIVILEGE.PRODUCT_VIEW,
  'Purchases/Supplier': PRIVILEGE.SUPPLIER_VIEW,
};

/** Privilege ID that controls visibility of each report card. */
export const REPORT_CARD_PRIVILEGE: Record<string, PrivilegeId> = {
  'Invoice Report': PRIVILEGE.INVOICE_REPRINT,
  'EOD': PRIVILEGE.EOD_REPORT_VIEW,
  'Customer Ledger': PRIVILEGE.CUSTOMER_LEDGER,
};

/** Privilege ID that controls visibility of each administrator card. */
export const ADMIN_CARD_PRIVILEGE: Record<string, PrivilegeId> = {
  'Company': PRIVILEGE.COMPANY_VIEW, // Show Company tab only when user has Company View (27)
  'User Roles': PRIVILEGE.USER_CREATE,
  'Settings': PRIVILEGE.SETTINGS,
};
